import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { useEffect } from 'react'

import { Slider, CurrencyInput, HelpIcon, TooltipText } from '../../../components'
import { numberToDollars } from '../../../utils'
import { useMakeChangesContext } from './MakeChangesContext'

const FieldContainer = styled.div`
  display: flex;
  padding: 12px 0;
  align-items: center;
  @media (max-width: 720px) {
    flex-wrap: wrap;
  }
`

const Field = styled.div`
  display: flex;
  flex-direction: row;
  flex: ${props => (props.readonly ? '1 1 auto' : '0 0 300px')};
  @media (max-width: 720px) {
    flex-direction: column;
  }
`

const HelpContainer = styled.div`
  display: inline-flex;
  flex-direction: column;
  flex: 1 1 325px;
  @media (max-width: 720px) {
    flex: 1 1 auto;
  }
`

const InputBox = styled.div`
  flex: ${props => (props.readonly ? '0 1 265px' : '0 1 300px')};
  @media (max-width: 720px) {
    flex: 1 1 100%;
  }
`

const SliderBox = styled.div`
  flex: 1 1 300px;
  @media (max-width: 720px) {
    flex: 0 1 100%;
    margin: 16px -12px 0;
  }
`

const PercentText = styled.span`
  font-size: 0.875rem;
  color: #677b84;
`

const InputLabel = styled.div`
  font-size: 0.875rem;
  color: #677b84;
  display: flex;
  align-items: center;
`

const ReadOnlyInput = styled.span`
  font-size: 1.125rem;
  color: ${p => p.theme.darkestGray};
  padding-right: 12px;
`

const InputContainer = styled.div`
  ${p =>
    p.editable &&
    css`
      display: flex;
      align-items: baseline;
      & > div:nth-of-type(1) {
        margin-right: 4px;
      }
    `};
  ${p =>
    !p.editable &&
    css`
      padding-bottom: ${p.displayHelp ? '5px' : '10px'};
    `};
  ${p =>
    p.invalid &&
    css`
      & span {
        color: red;
      }
    `};
`

const ContribLimitError = styled.p`
  color: red;
  font-size: 14px;
  font-weight: 100;
  margin: 0px;
`

const SliderInputField = ({
  inputLabel,
  percentLabel,
  inputValue,
  percentValue,
  onChange,
  readonly,
  displayAnnualCompLimitHelp,
  displayContribAppearsHighHelp,
  percentContribLimitExceededKey,
  displayPercentContribLimitExceeded,
  limits,
  warning,
  maxValue = 92,
}) => {
  const {
    setPercentContribLimitExceeded,
    clearPercentContribLimitExceeded,
    setPercentContribLimit,
  } = useMakeChangesContext()

  useEffect(() => {
    if (!percentContribLimitExceededKey || !setPercentContribLimitExceeded) return

    setPercentContribLimitExceeded(
      percentContribLimitExceededKey,
      Boolean(displayPercentContribLimitExceeded)
    )

    return () => {
      if (!clearPercentContribLimitExceeded) return
      clearPercentContribLimitExceeded(percentContribLimitExceededKey)
    }
  }, [
    percentContribLimitExceededKey,
    displayPercentContribLimitExceeded,
    setPercentContribLimitExceeded,
    clearPercentContribLimitExceeded,
  ])

  useEffect(() => {
    if (!setPercentContribLimit) return
    setPercentContribLimit(maxValue)
  }, [maxValue, setPercentContribLimit])

  return (
    <>
      <FieldContainer>
        <Field readonly={readonly}>
          <InputBox readonly={readonly}>
            <InputContainer
              editable={!readonly}
              invalid={percentValue > 100 || displayPercentContribLimitExceeded}
              displayHelp={displayAnnualCompLimitHelp || displayContribAppearsHighHelp}>
              {!readonly && (
                <CurrencyInput
                  name='input'
                  width='88px'
                  value={inputValue}
                  onChange={() => {}}
                  disabled
                />
              )}
              {readonly && <ReadOnlyInput>${inputValue}</ReadOnlyInput>}
              <PercentText>
                ({numberToDollars(percentValue, true)}% {percentLabel})
              </PercentText>
            </InputContainer>
            <InputLabel>
              {inputLabel} {warning && <span style={{ marginLeft: 10 }}>{warning}</span>}
            </InputLabel>
          </InputBox>
          {(displayContribAppearsHighHelp || displayAnnualCompLimitHelp) && (
            <HelpContainer>
              {displayContribAppearsHighHelp && (
                <HelpIcon
                  helpLabel={'Contribution appears higher than permitted'}
                  tooltip={TooltipText.whyDoesTheTotalContributionAppearToBeMoreThanWhatsAllowed()}
                />
              )}
              {displayAnnualCompLimitHelp && (
                <HelpIcon
                  helpLabel={'Annual compensation limit applied'}
                  tooltip={TooltipText.annualCompensationLimitApplied({
                    compensationLimit: limits.annualContributionLimit,
                    currentYear: limits.year,
                    electiveDeferrals: limits.dlr401k,
                    catchUp50Up: limits.catchupLimitAmount,
                    catchUp60To63: limits.maximumAmount,
                  })}
                />
              )}
            </HelpContainer>
          )}
        </Field>

        {!readonly && (
          <SliderBox>
            <Slider
              value={percentValue > maxValue ? maxValue : percentValue || 0}
              onChange={onChange}
              step={1}
              min={0}
              max={maxValue}
            />
          </SliderBox>
        )}
      </FieldContainer>
      {displayPercentContribLimitExceeded && (
        <ContribLimitError>Total contributions cannot exceed {maxValue}%.</ContribLimitError>
      )}
    </>
  )
}

export default SliderInputField
